import React, { useState } from 'react';
import { Table, Tabs, Progress } from 'antd';
import NoSavingsYet from '../../pages/user/dashboard/savings/NoSavingsYet';
import type { ColumnsType } from 'antd/es/table';
import { formatToNaira } from 'functions/currencyConveter';
import SavingsTabModal from 'components/shared/modal/SavingsTabModal';
import WithdrawSavingsPopUs from 'pages/user/dashboard/savings/WithdrawSavingsPopUs';
import AddSavingsModal from 'pages/user/dashboard/savings/AddSavingsModal';
import { sendCatchFeedback, sendFeedback } from 'functions/feedback';
// import ReinvestSavingsModal from 'pages/user/dashboard/savings/ReinvestSavingsModal';
import { appAxios } from 'api/axios';
import Button from 'components/shared/button';
import ReinvestModal from 'components/shared/modal/ReinvestModal';

interface SavingsItem {
  id: string;
  goalName: string;
  targetAmount: number;
  savedAmount: number;
  startDate: string;
  endDate: string;
  status: string;
  duration: number;
  isAchieved: boolean;
  createdAt: string;
  imageUrl: string;
}

interface SingleGoalData {
  goalName: string;
  targetAmount: number;
  preferedSavingAmount: number;
  startDate: string;
  endDate: string;
  nextSavingDate: string;
  status: string;
  duration: number;
  savingsFrequency: string;
  savingType: string;
  interest: number;
  savingMethod: string;
  autoWithDraw: boolean;
}

interface SavedItemsProps {
  allSavings: SavingsItem[];
  activeTab: string;
  setActiveTab: (tab: string) => void;
}

// Helper function to calculate duration in days between two dates
const calculateDurationInDays = (
  startDate: string,
  endDate: string
): number => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffTime = Math.abs(end.getTime() - start.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

const formatDuration = (startDate: string, endDate: string): string => {
  const days = calculateDurationInDays(startDate, endDate);

  if (days < 7) {
    return `${days} day${days !== 1 ? 's' : ''}`;
  } else if (days < 30) {
    const weeks = Math.floor(days / 7);
    const remainingDays = days % 7;
    if (remainingDays === 0) {
      return `${weeks} week${weeks !== 1 ? 's' : ''}`;
    }
    return `${weeks} week${weeks !== 1 ? 's' : ''} and ${remainingDays} day${remainingDays !== 1 ? 's' : ''}`;
  } else if (days < 365) {
    const months = Math.floor(days / 30);
    const remainingDays = days % 30;
    if (remainingDays === 0) {
      return `${months} month${months !== 1 ? 's' : ''}`;
    }

    // If remaining days can form complete weeks
    if (remainingDays >= 7) {
      const weeks = Math.floor(remainingDays / 7);
      return `${months} month${months !== 1 ? 's' : ''} and ${weeks} week${weeks !== 1 ? 's' : ''}`;
    }

    return `${months} month${months !== 1 ? 's' : ''} and ${remainingDays} day${remainingDays !== 1 ? 's' : ''}`;
  } else {
    const years = Math.floor(days / 365);
    const remainingDays = days % 365;

    if (remainingDays === 0) {
      return `${years} year${years !== 1 ? 's' : ''}`;
    }

    const months = Math.floor(remainingDays / 30);
    if (months === 0) {
      return `${years} year${years !== 1 ? 's' : ''}`;
    }

    return `${years} year${years !== 1 ? 's' : ''} and ${months} month${months !== 1 ? 's' : ''}`;
  }
};

const SavingsTabs: React.FC<SavedItemsProps> = ({
  allSavings,
  activeTab = 'active',
  setActiveTab,
}) => {
  // State to track active tab
  // const [activeTab, setActiveTab] = useState('ongoing');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSavings, setSelectedSavings] = useState<SavingsItem | null>(
    null
  );
  const [isWithdrawPopupOpen, setIsWithdrawPopupOpen] = useState(false);
  const [isAddSavingsModalOpen, setIsAddSavingsModalOpen] = useState(false);
  const [selectedGoalId, setSelectedGoalId] = useState<string | null>(null);
  const [isReinvestModalOpen, setIsReinvestModalOpen] = useState(false);

  const [singleGoal, setSingleGoal] = useState<SingleGoalData | null>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5; // Adjust as needed

  const handleOpenModal = async (item: SavingsItem) => {
    setSelectedSavings(item);
    await fetchSingleSavingsData(item.id);
    setIsModalOpen(true);
  };
  const fetchSingleSavingsData = async (goalId: string) => {
    try {
      const response = await appAxios.get(
        `get-single-customer-savings?goalId=${goalId}`
      );
      console.log('Single Savings Data:', response.data);
      // Check if data is nested inside an object
      const goalData = response.data.data || response.data;
      console.log('Processed goal data:', goalData);
      setSingleGoal(goalData);
    } catch (error) {
      console.error('Error fetching single savings data:', error);
      sendCatchFeedback(error);
    }
  };

  // Define columns for the table
  const columns: ColumnsType<SavingsItem> = [
    { title: 'Savings Name', dataIndex: 'goalName', key: 'goalName' },
    {
      title: 'Maturity Date',
      dataIndex: 'endDate',
      key: 'endDate',
      render: (endDate: string) =>
        new Date(endDate).toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        }),
      // render: (endDate: string) => new Date(endDate).toLocaleString(),
    },
    {
      title: 'Target Amount',
      dataIndex: 'targetAmount',
      key: 'targetAmount',
      render: (targetAmount: number) => (
        <p className="">{formatToNaira(targetAmount)}</p>
      ),
    },
    {
      title: 'Saved Amount',
      dataIndex: 'savedAmount',
      key: 'savedAmount',
      render: (savedAmount: number) => (
        <p className="">{formatToNaira(savedAmount)}</p>
      ),
    },
    {
      title: 'Goal Level',
      dataIndex: 'goalLevel',
      key: 'goalLevel',
      render: (_, record) => (
        <Progress
          percent={(record.savedAmount / record.targetAmount) * 100}
          status="active"
          showInfo={true}
        />
      ),
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <div className="flex  gap-4">
          <Button
            className="text-xs px-1 font-medium cursor-pointer hover:underline"
            onClick={() => handleOpenModal(record)}
          >
            View Details
          </Button>
          {record.status === 'Active' && (
            <Button
              className="text-xs font-medium text-white cursor-pointer bg-blue-500/90 hover:underline hover:bg-blue-500/100"
              onClick={() => handleAddSavings(record.id)}
            >
              Credit Savings
            </Button>
          )}
        </div>
      ),
    },
  ];

  // Pagination Logic
  const totalPages = Math.ceil(allSavings.length / itemsPerPage);

  const tabs = [
    { key: 'ongoing', label: 'Ongoing', status: 'Active' },
    { key: 'RolledOver', label: 'Rolled-over', status: 'RolledOver' },
    { key: 'Completed', label: 'Completed', status: 'Completed' },
  ];

  const filteredSavings = allSavings.filter(
    (item) =>
      item.status.trim() === tabs.find((tab) => tab.key === activeTab)?.status
  );

  const displayedSavings = filteredSavings.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const currentStatus = tabs.find((tab) => tab.key === activeTab)?.status || '';

  const handleAddSavings = (goalId: string) => {
    setSelectedGoalId(goalId);
    setIsAddSavingsModalOpen(true);
  };

  const handleReinvest = () => {
    if (selectedSavings) {
      setIsModalOpen(false);
      setIsReinvestModalOpen(true);
    }
  };

  const handleConfirmReinvest = async () => {
    try {
      // Add your reinvest API call here
      setIsReinvestModalOpen(false);
      sendFeedback('Reinvestment successful', 'success');
    } catch (error) {
      sendCatchFeedback(error);
    }
  };

  return (
    <div className="p-6 mt-6 bg-white rounded-lg shadow-md">
      {/* Tabs Navigation */}
      <div className="flex border-b-4 border-gray-300">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            className={`px-6 py-3 text-lg font-medium focus:outline-0 transition-all 
              ${
                activeTab === tab.key
                  ? 'border-b-4 border-[#31005C] text-[#31005C]'
                  : 'text-gray-500 hover:text-gray-800'
              }`}
            onClick={() => {
              setActiveTab(tab.key);
              setCurrentPage(1);
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {filteredSavings.length > 0 ? (
        <Table
          columns={columns}
          dataSource={displayedSavings}
          pagination={false}
          scroll={{ x: 'max-content' }}
          rowKey="id"
        />
      ) : (
        <NoSavingsYet />
      )}

      {/* Pagination Controls */}
      <div className="flex justify-between items-center mt-4">
        <div className="flex gap-2">
          <button
            className="px-4 py-2 text-gray-700 bg-gray-200 rounded disabled:opacity-50"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
          >
            Previous
          </button>
          <button
            className="px-4 py-2 bg-[#EFA005] text-white rounded disabled:opacity-50 disabled:bg-gray-200 disabled:text-gray-600"
            disabled={currentPage === totalPages || totalPages === 0}
            onClick={() =>
              setCurrentPage((prev) => Math.min(prev + 1, totalPages))
            }
          >
            Next
          </button>
        </div>

        <span className="text-gray-600">
          Page {totalPages > 0 ? currentPage : 0} of {totalPages}
        </span>
      </div>

      {selectedSavings && (
        <SavingsTabModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          title={selectedSavings.goalName}
          amount={formatToNaira(selectedSavings.targetAmount)}
          status={currentStatus}
          singleGoal={singleGoal}
          details={[
            {
              label: 'Start Date',
              value: new Date(selectedSavings.startDate).toLocaleDateString(
                'en-GB',
                { day: '2-digit', month: 'short', year: 'numeric' }
              ),
            },
            {
              label: 'Maturity Date',
              value: new Date(selectedSavings.endDate).toLocaleDateString(
                'en-GB',
                { day: '2-digit', month: 'short', year: 'numeric' }
              ),
            },
            {
              label: 'Duration',
              value: formatDuration(
                selectedSavings.startDate,
                selectedSavings.endDate
              ),
            },
            {
              label: 'Saved Amount',
              value: formatToNaira(selectedSavings.savedAmount),
            },
          ]}
          primaryAction={{
            text: currentStatus === 'RolledOver' ? 'Reinvest' : 'Withdraw',
            onClick:
              currentStatus === 'RolledOver'
                ? handleReinvest
                : () => {
                    if (selectedSavings && selectedSavings.savedAmount > 0) {
                      setIsModalOpen(false);
                      setIsWithdrawPopupOpen(true);
                    } else {
                      sendFeedback(
                        'You cannot withdraw because there are no savings available.',
                        'error'
                      );
                    }
                  },
          }}
          secondaryAction={{
            text: 'Cancel',
            onClick: () => setIsModalOpen(false),
          }}
        />
      )}

      {isWithdrawPopupOpen && selectedSavings && (
        <WithdrawSavingsPopUs
          isOpen={isWithdrawPopupOpen}
          onClose={() => setIsWithdrawPopupOpen(false)}
          id={selectedSavings?.id}
        />
      )}

      {selectedGoalId && (
        <AddSavingsModal
          isOpen={isAddSavingsModalOpen}
          onClose={() => {
            setIsAddSavingsModalOpen(false);
            setSelectedGoalId(null);
          }}
          goalId={selectedGoalId}
        />
      )}

      {selectedSavings && (
        <ReinvestModal
          isOpen={isReinvestModalOpen}
          onClose={() => setIsReinvestModalOpen(false)}
          title={selectedSavings.goalName}
          amount={formatToNaira(selectedSavings.targetAmount)}
          details={[
            {
              label: 'Start Date',
              value: new Date(selectedSavings.startDate).toLocaleDateString(
                'en-GB',
                { day: '2-digit', month: 'short', year: 'numeric' }
              ),
            },
            {
              label: 'Maturity Date',
              value: new Date(selectedSavings.endDate).toLocaleDateString(
                'en-GB',
                { day: '2-digit', month: 'short', year: 'numeric' }
              ),
            },
            {
              label: 'Duration',
              value: formatDuration(
                selectedSavings.startDate,
                selectedSavings.endDate
              ),
            },
            {
              label: 'Saved Amount',
              value: formatToNaira(selectedSavings.savedAmount),
            },
            {
              label: 'Goal Level',
              value: `${Math.round((selectedSavings.savedAmount / selectedSavings.targetAmount) * 100)}%`,
              isSuccess: true,
            },
            ...(singleGoal
              ? [
                  {
                    label: 'Interest Earned',
                    value: formatToNaira(singleGoal.interest || 0),
                    isSuccess: true,
                    isInterest: true,
                  },
                ]
              : []),
          ]}
          onReinvest={handleConfirmReinvest}
        />
      )}
    </div>
  );
};

export default SavingsTabs;
